import api from './api';
import type { RefillAssessmentStatus } from '../components/counseling/RefillAssessmentChip';
// Checklist shape lives in one place (see CLAUDE.md: one source of truth).
import type {
  CounselingChecklist,
  CounselingChecklistItem,
} from '../components/counseling/counselingChecklist';

export type { RefillAssessmentStatus };
export type { CounselingChecklist, CounselingChecklistItem };

/**
 * GP-927 — one row of the "Refill Assessment" due-list (task-queue tab). Mirrors
 * the backend `RefillAssessmentDueItem`.
 */
export interface RefillAssessmentDueItem {
  patientId: string;
  patientName: string | null;
  drugName: string | null;
  din: string | null;
  rxNumber: string | null;
  lastAssessedAt: string;
  daysSinceLastAssessment: number;
}

// ---- Types matching backend CounselingRecord entity ----

export interface CounselingRecord {
  id: string;
  pharmacyId: string;
  orderId?: string;
  rxNumber?: string;
  patientId?: string;
  patientName?: string;
  drugName?: string;
  din?: string;
  doctorName?: string;
  status: 'pending' | 'accepted' | 'declined' | 'completed';
  reason: 'new_rx' | 'transfer' | 'other';
  counselingBy: 'patient' | 'caregiver' | 'representative';
  method: 'by_phone' | 'in_person' | 'video' | 'written';
  checklist: CounselingChecklist;
  comments?: string;
  createdBy?: string;
  completedBy?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  taskId?: string;
  declinedReason?: string;
  agentName?: string;
  agentRelationship?: string;
  sessionStartedAt?: string;
  sessionDurationSeconds?: number;
  dateCounselled?: string;
  electronicSignatureId?: string;
}

export interface CreateCounselingDto {
  pharmacyId: string;
  orderId?: string;
  rxNumber?: string;
  patientId?: string;
  patientName?: string;
  drugName?: string;
  din?: string;
  doctorName?: string;
  status?: string;
  reason?: string;
  counselingBy?: string;
  method?: string;
  checklist?: CounselingChecklist;
  comments?: string;
  declinedReason?: string;
  agentName?: string;
  agentRelationship?: string;
  sessionStartedAt?: string;
  sessionDurationSeconds?: number;
  dateCounselled?: string;
  electronicSignatureId?: string;
  // Task Queue passthrough fields — present on the backend CreateCounselingDto and
  // already posted by ClinicalVerificationDialog / CounselingToggle / the Task
  // Queue's create-task path, but previously missing here, so every caller had to
  // build the body inline. Declared once so the type IS the contract.
  entryMethod?: string;
  assignedTo?: string;
  patientDob?: string;
  krollId?: string;
  foxId?: string;
  linkStatus?: string;
  counselingOrigin?: string;
  /** GP-1266: pharmacist-chosen reason, stamped on the auto-created queue task. */
  queueReason?: string;
  /** Plain 'YYYY-MM-DD' — never a timestamp (see CLAUDE.md date rules). */
  deliveryDate?: string;
  orderNumber?: string;
}

/**
 * GP-1222 — the shape the "Create New Task" dialog hands to the counseling
 * pipeline when creating a Patient Counselling task. Shared by the Task Queue
 * (`/tasks`) and the Patient 360 Order History "Add Counseling Task" action so
 * the `/api/counseling` POST body lives in exactly one place (see CLAUDE.md:
 * one source of truth) — a second copy would drift the moment either surface
 * gained a field.
 */
export interface CounselingTaskDraft {
  patientId?: string;
  patientName?: string;
  patientDob?: string;
  krollId?: string;
  foxId?: string;
  linkStatus?: string;
  rxNumber?: string;
  orderId?: string;
  orderNumber?: string;
  drugName?: string;
  din?: string;
  doctorName?: string;
  troubleshootingReason?: string;
  description?: string;
  assignedTo?: string;
  /** Where the task was created from (analytics only). Defaults to 'task_queue'. */
  counselingOrigin?: string;
}

/**
 * GP-1487 — outcome of one bulk "Send to task queue" request. Partial success is
 * reported per item so the caller can name the orders that did NOT land instead
 * of claiming the whole batch worked. Mirrors the backend
 * `BulkQueueCounselingResult`.
 */
export interface BulkQueueCounselingResult {
  queued: Array<{ orderId?: string; rxNumber?: string; counselingId: string }>;
  failed: Array<{ orderId?: string; rxNumber?: string; message: string }>;
}

/**
 * GP-1487 — one counseling row as a queue grid needs it: enough to tell whether
 * that (order, Rx) is already sitting in the Task Queue. Mirrors the backend
 * `CounselingStatusForOrder`.
 */
export interface CounselingStatusForOrder {
  orderId: string;
  rxNumber: string | null;
  status: string;
  reason: string;
  declinedReason: string | null;
}

export interface UpdateCounselingDto {
  status?: string;
  counselingBy?: string;
  method?: string;
  checklist?: CounselingChecklist;
  comments?: string;
  declinedReason?: string;
  agentName?: string;
  agentRelationship?: string;
  sessionDurationSeconds?: number;
  dateCounselled?: string;
  electronicSignatureId?: string;
}

// ---- Legacy interfaces (kept for PHOXCounseling page backwards compat) ----

export interface CounselingSession {
  id: string;
  patientId: string;
  patientName: string;
  phoxOrderId: string;
  drugName: string;
  sessionDate: Date;
  pharmacistId: string;
  pharmacistName: string;
  sessionType: 'initial' | 'follow-up' | 'refill' | 'special';
  duration: number;
  method: 'in-person' | 'phone' | 'video' | 'written';
  language: string;
  complianceScore: number;
  understandingLevel: 'excellent' | 'good' | 'fair' | 'poor';
  questionsAsked: string[];
  concernsRaised: string[];
  requirements: CounselingRequirement[];
  topicsCompleted: string[];
  notes: string;
  patientSignature?: string;
  pharmacistSignature?: string;
  signatureDate?: Date;
  followUpRequired: boolean;
  followUpDate?: Date;
  followUpNotes?: string;
}

export interface CounselingRequirement {
  id: string;
  topic: string;
  category: 'mandatory' | 'recommended' | 'optional';
  completed: boolean;
  notes?: string;
}

export interface CounselingTemplate {
  id: string;
  name: string;
  drugClass: string;
  language: string;
  topics: string[];
  content: string;
  estimatedDuration: number;
  lastUpdated: Date;
}

export interface CounselingMetrics {
  totalSessions: number;
  completedToday: number;
  pendingRequired: number;
  averageComplianceScore: number;
  averageDuration: number;
  topLanguages: { language: string; count: number }[];
}

export interface CounselingHistory {
  sessionId: string;
  date: Date;
  pharmacist: string;
  complianceScore: number;
  notes: string;
  followUpCompleted: boolean;
}

class CounselingService {
  // ---- Real API methods ----

  async createRecord(dto: CreateCounselingDto): Promise<CounselingRecord> {
    const response = await api.post('/api/counseling', dto);
    return response.data?.data;
  }

  /**
   * GP-1222 — create a pending Patient Counselling task through the counseling
   * pipeline (which auto-creates the queue task, drives the status chip, and is
   * visible in Counseling History), from the "Create New Task" dialog payload.
   * `reason: 'other'` and `status` omitted (defaults to PENDING) mirror the
   * original Task Queue create path so both surfaces behave identically.
   */
  async createCounselingTask(pharmacyId: string, draft: CounselingTaskDraft): Promise<CounselingRecord> {
    const response = await api.post('/api/counseling', {
      pharmacyId,
      patientId: draft.patientId,
      patientName: draft.patientName,
      patientDob: draft.patientDob || undefined,
      krollId: draft.krollId,
      foxId: draft.foxId,
      linkStatus: draft.linkStatus,
      rxNumber: draft.rxNumber,
      orderId: draft.orderId,
      orderNumber: draft.orderNumber,
      drugName: draft.drugName,
      din: draft.din,
      doctorName: draft.doctorName,
      reason: 'other',
      comments: [draft.troubleshootingReason, draft.description].filter(Boolean).join(' — ') || undefined,
      counselingOrigin: draft.counselingOrigin || 'task_queue',
      entryMethod: 'Manual',
      assignedTo: draft.assignedTo,
    });
    return response.data?.data;
  }

  /**
   * GP-1487 — send many (order, Rx) pairs to the Task Queue in ONE request.
   *
   * Backs the Clinical Verification queue's per-row and bulk "Send to task
   * queue" actions. Deliberately NOT a loop of `createRecord()`: 25 orders
   * meant 25 round trips, which is the cost the ticket is about. Each item is
   * the same payload the order-detail footer's "Queue Counseling" posts, so both
   * surfaces drive one server-side code path.
   */
  async bulkQueueCounseling(items: CreateCounselingDto[]): Promise<BulkQueueCounselingResult> {
    if (items.length === 0) return { queued: [], failed: [] };
    const response = await api.post('/api/counseling/bulk-queue', { items });
    return response.data?.data || { queued: [], failed: [] };
  }

  /**
   * GP-1487 — counseling records for a whole page of orders in ONE request, so a
   * queue grid can show which rows are already in the Task Queue.
   *
   * skipQueueBump: this POST is a READ fired from an effect that runs on every
   * queue paint. Without the opt-out the axios interceptor treats it as a
   * mutation and bumps the queue-refresh bus → refetch → repaint → refire, the
   * infinite loop GP-927 hit with the refill-assessment batch.
   */
  async getCounselingStatusByOrders(
    pharmacyId: string,
    orderIds: string[],
  ): Promise<CounselingStatusForOrder[]> {
    if (!pharmacyId || orderIds.length === 0) return [];
    const response = await api.post(
      '/api/counseling/status/batch',
      { pharmacyId, orderIds },
      { skipQueueBump: true },
    );
    return response.data?.data || [];
  }

  async getRecordsByPharmacy(pharmacyId: string, status?: string): Promise<CounselingRecord[]> {
    const params = status ? `?status=${status}` : '';
    const response = await api.get(`/api/counseling/${pharmacyId}${params}`);
    return response.data?.data || [];
  }

  /**
   * Every counseling record for a patient (newest first), across orders and Rx
   * numbers. One wrapper for the `by-patient` route so the surfaces that render
   * counseling — Counseling History, the RX History timeline, and the Rx Detail
   * dialog (GP-1358) — all read it the same way instead of each hand-rolling an
   * `api.get()` and its own response-shape handling.
   */
  async getRecordsByPatient(patientId: string): Promise<CounselingRecord[]> {
    const response = await api.get(`/api/counseling/by-patient/${patientId}`);
    return response.data?.data || [];
  }

  async getRecordById(pharmacyId: string, id: string): Promise<CounselingRecord> {
    const response = await api.get(`/api/counseling/${pharmacyId}/${id}`);
    return response.data?.data;
  }

  async getRecordByOrder(orderId: string): Promise<CounselingRecord | null> {
    try {
      const response = await api.get(`/api/counseling/by-order/${orderId}`);
      return response.data?.data || null;
    } catch {
      return null;
    }
  }

  async updateRecord(pharmacyId: string, id: string, dto: UpdateCounselingDto): Promise<CounselingRecord> {
    const response = await api.patch(`/api/counseling/${pharmacyId}/${id}`, dto);
    return response.data?.data;
  }

  async finalizeRecord(pharmacyId: string, id: string): Promise<CounselingRecord> {
    const response = await api.post(`/api/counseling/${pharmacyId}/${id}/finalize`);
    return response.data?.data;
  }

  /**
   * GP-1164 — remove a redundant/duplicate counseling entry. Soft-delete on the
   * backend (audited tombstone): the record drops out of the patient's Counseling
   * History but is retained, never hard-deleted.
   */
  async deleteRecord(pharmacyId: string, id: string): Promise<void> {
    await api.delete(`/api/counseling/${pharmacyId}/${id}`);
  }

  /**
   * GP-927 — resolve the periodic refill-reassessment status for a page of
   * patients in ONE request (instead of one per row). Returns one status per
   * requested patient, in input order.
   */
  async getRefillAssessmentBatch(
    pharmacyId: string,
    patients: Array<{ patientId: string; din?: string }>,
  ): Promise<RefillAssessmentStatus[]> {
    if (!pharmacyId || patients.length === 0) return [];
    // skipQueueBump: this POST is a READ (batch status lookup) fired from an
    // effect on every queue paint. Without the opt-out, the axios interceptor
    // treats it as a mutation and bumps the queue-refresh bus, which refetches
    // the queue, which repaints, which refires this effect — an infinite
    // ~1.4s-period refetch loop on every open CV tab (GP-927 regression).
    const response = await api.post(
      '/api/counseling/refill-assessment/batch',
      {
        pharmacyId,
        patients,
      },
      { skipQueueBump: true },
    );
    return response.data?.data || [];
  }

  /**
   * GP-927 — the "Refill Assessment" due-list: every (patient, drug) whose
   * latest completed assessment is past the interval, most overdue first.
   */
  async getRefillAssessmentDueList(pharmacyId: string): Promise<RefillAssessmentDueItem[]> {
    if (!pharmacyId) return [];
    const response = await api.get(`/api/counseling/refill-assessment-due/${pharmacyId}`);
    return response.data?.data || [];
  }

  // ---- Legacy mock methods (used by PHOXCounseling page) ----

  async getSessionsByPatient(_patientId: string): Promise<CounselingSession[]> {
    return [];
  }

  async getSessionById(_sessionId: string): Promise<CounselingSession | null> {
    return null;
  }

  async getTemplates(_drugClass?: string, _language?: string): Promise<CounselingTemplate[]> {
    return [];
  }

  async createSession(session: Partial<CounselingSession>): Promise<CounselingSession> {
    const response = await api.post('/api/counseling/sessions', session);
    return response.data;
  }

  async updateSession(sessionId: string, updates: Partial<CounselingSession>): Promise<void> {
    await api.patch(`/api/counseling/sessions/${sessionId}`, updates);
  }

  async getMetrics(): Promise<CounselingMetrics> {
    return {
      totalSessions: 0,
      completedToday: 0,
      pendingRequired: 0,
      averageComplianceScore: 0,
      averageDuration: 0,
      topLanguages: [],
    };
  }

  async getPatientHistory(_patientId: string): Promise<CounselingHistory[]> {
    return [];
  }

  async saveSignature(sessionId: string, signatureData: string, signatureType: 'patient' | 'pharmacist'): Promise<void> {
    await api.post(`/api/counseling/sessions/${sessionId}/signature`, {
      signatureData,
      signatureType,
      timestamp: new Date(),
    });
  }

  async scheduleFollowUp(sessionId: string, followUpDate: Date, notes: string): Promise<void> {
    await api.post(`/api/counseling/sessions/${sessionId}/follow-up`, {
      followUpDate,
      notes,
    });
  }

  async markRequirementComplete(sessionId: string, requirementId: string, notes?: string): Promise<void> {
    await api.patch(`/api/counseling/sessions/${sessionId}/requirements/${requirementId}`, {
      completed: true,
      notes,
      completedAt: new Date(),
    });
  }
}

export default new CounselingService();
